
import { useCallback } from 'react';
import { Subscription } from '../types';
import { useSubscriptionFetchLogic } from './useSubscriptionFetchLogic';

interface UseSubscriptionFetchProps {
  userId: string | undefined;
  setIsLoading: (isLoading: boolean) => void;
  setIsSubscribed: (isSubscribed: boolean) => void;
  setCurrentSubscription: (subscription: Subscription | null) => void;
  setLastChecked: (date: Date | null) => void;
  setIsRetrying: (isRetrying: boolean) => void;
  isRefreshingRef: React.MutableRefObject<boolean>;
  isMountedRef: React.MutableRefObject<boolean>;
  fetchInProgressRef: React.MutableRefObject<boolean>;
  debounceTimeoutRef: React.MutableRefObject<number | null>;
  lastFetchTimeRef: React.MutableRefObject<number>;
  timeoutRef: React.MutableRefObject<number | null>;
}

export function useSubscriptionFetch({
  userId,
  setIsLoading,
  setIsSubscribed,
  setCurrentSubscription,
  setLastChecked,
  setIsRetrying,
  isRefreshingRef,
  isMountedRef,
  fetchInProgressRef,
  debounceTimeoutRef,
  lastFetchTimeRef,
  timeoutRef
}: UseSubscriptionFetchProps) {
  
  const {
    executeSubscriptionFetch,
    cleanupDebounceTimeouts,
    shouldThrottleRequest,
    isFetchInProgress
  } = useSubscriptionFetchLogic({
    userId,
    setIsLoading,
    setIsSubscribed,
    setCurrentSubscription,
    setLastChecked,
    setIsRetrying,
    isRefreshingRef,
    isMountedRef,
    fetchInProgressRef,
    debounceTimeoutRef,
    lastFetchTimeRef
  });
  
  const cleanupTimeouts = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    cleanupDebounceTimeouts();
  }, [timeoutRef, cleanupDebounceTimeouts]);

  const fetchSubscriptionStatus = useCallback(async (showToast = false) => {
    console.log('=== FETCH SUBSCRIPTION STATUS CALLED ===');
    console.log('showToast:', showToast);
    console.log('userId:', userId);
    
    // Skip throttling for forced refreshes
    if (!showToast && shouldThrottleRequest()) {
      console.log(`Fetch throttled - last fetch was ${Date.now() - lastFetchTimeRef.current}ms ago`);
      return Promise.resolve(false);
    }
    
    if (debounceTimeoutRef.current) {
      window.clearTimeout(debounceTimeoutRef.current);
    }
    
    return new Promise<boolean>((resolve) => {
      debounceTimeoutRef.current = window.setTimeout(async () => {
        if (!showToast && isFetchInProgress()) {
          console.log('Fetch already in progress, skipping');
          resolve(false);
          return;
        }
        
        cleanupTimeouts();
        
        const result = await executeSubscriptionFetch(showToast);
        resolve(result);
      }, showToast ? 0 : 150); // No delay for forced refreshes
    });
  }, [
    userId, 
    debounceTimeoutRef,
    shouldThrottleRequest,
    isFetchInProgress,
    cleanupTimeouts,
    lastFetchTimeRef,
    executeSubscriptionFetch
  ]);

  return {
    fetchSubscriptionStatus,
    cleanupTimeouts
  };
}
